import { AsyncPipe, NgClass } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { AsyncSubject, BehaviorSubject } from 'rxjs';
import {
	debounceTime,
	filter,
	finalize,
	switchMap,
	take,
	takeUntil,
	tap,
} from 'rxjs/operators';
import spacetime, { type Spacetime } from 'spacetime';
import { StackedVerticalBarChartComponent } from 'src/lib/charts/stacked-vertical-bar-chart/stacked-vertical-bar-chart.component';
import { SpaceFormatPipe } from 'src/lib/pipes/spacetime/space-format.pipe';
import { SpaceMapPipe } from 'src/lib/pipes/spacetime/space-map.pipe';
import { UserNamePipe } from 'src/lib/pipes/user-name.pipe';
import { StudentAssignmentsModel } from 'src/lib/services/api/students/student-assignments.model';
import { StudentsService } from 'src/lib/services/api/students/students.service';
import { UsersService } from 'src/lib/services/api/users/users.service';
import { mergeStrings } from 'src/lib/utilities/array';
import { calendarDate } from 'src/lib/utilities/spacetime';
import { BillboardCollapsableDirective } from '../../global/billboard-collapsable/billboard-collapsable.directive';
import { BillboardLabelDirective } from '../../global/billboard-label/billboard-label.directive';
import { SizeMonitorService } from '../../global/size-monitor/size-monitor.service';
import { SpinWhileDirective } from '../../layout/spin-while/spin-while.directive';
import { PaginationHelper } from '../../tables/pagination-table/pagination-helper';
import { PaginationTableApi } from '../../tables/pagination-table/pagination-table-api';
import { PaginationTableConfig } from '../../tables/pagination-table/pagination-table-config';
import { PaginationTableModule } from '../../tables/pagination-table/pagination-table.module';
import { PaginationTableId } from '../../tables/pagination-table/types/pagination-table-id';

interface LunchboxPagesOverviewNxgChartMultiModel {
	name: string;
	series: {
		name: string;
		value: number;
	}[];
}

@Component({
	selector: 'ae-student-completed-assignments-chart',
	imports: [
		AsyncPipe,
		BillboardCollapsableDirective,
		BillboardLabelDirective,
		NgClass,
		PaginationTableModule,
		SpaceFormatPipe,
		SpaceMapPipe,
		SpinWhileDirective,
		StackedVerticalBarChartComponent,
		UserNamePipe,
	],
	templateUrl: './student-completed-assignments-chart.component.html',
	styleUrl: './student-completed-assignments-chart.component.scss',
})
export class StudentCompletedAssignmentsChartComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	private _unsubscribe$ = new AsyncSubject<null>();
	currentStudentId = input.required<number>();
	isStudentView = input.required<boolean>();

	@ViewChild('graphContainer', { static: true })
	graphContainer: ElementRef<HTMLElement>;

	public assignmentDetails = new BehaviorSubject<StudentAssignmentsModel[]>(
		null,
	);

	public studentAssignmentsData: LunchboxPagesOverviewNxgChartMultiModel[] = [];
	public view = undefined;
	public colors = [
		'#dfc824',
		'#8781bd',
		'#c4285e',
		'#3b8cbb',
		'#894564',
		'#71b438',
		'#068587',
		'#f1632a',
		'#f2b134',
		'#4fb99f',
		'#78e2f0',
		'#788df8',
		'#78b159',
		'#b07994',
		'#1f8789',
	];
	private colorMap = new Map<string, string>(); // K: Class Title,V: Color Hash
	public colorOverride: { name: string; value: string }[] = [];
	private colorTicker = 0;

	public fromDate: Spacetime;
	public toDate: Spacetime;
	public atCurrentDate: boolean = true;

	public loadingAssignments: boolean = true;
	public hasError: boolean = false;
	public noStudentData: boolean = false;
	public showDetails: boolean = false;

	public widthPercent = 100;
	public heightPercent = 100;

	public tableApi: PaginationTableApi;
	public tableConfig: PaginationTableConfig<StudentAssignmentsModel>;
	public paginationHelper: PaginationHelper<StudentAssignmentsModel>;

	public timezone: string;

	constructor(
		private cdr: ChangeDetectorRef,
		private studentService: StudentsService,
		private usersService: UsersService,

		private sizeMonitorService: SizeMonitorService,
	) {}

	ngOnInit(): void {
		this.initTable();
		if (this.currentStudentId() == null) {
			this.hasError = true;
		} else {
			this.fromDate = spacetime.now().add(-13, 'date');
			this.toDate = spacetime.now();
			this.getAssignmentData();

			this.sizeMonitorService.pixels$
				.pipe(debounceTime(100), takeUntil(this._unsubscribe$))
				.subscribe(() => {
					this.rescaleChart();
				});
		}
	}

	ngAfterViewInit(): void {
		this.rescaleChart();
	}

	private initTable = () => {
		this.paginationHelper =
			PaginationHelper.CreateSimple<StudentAssignmentsModel>();

		this.tableConfig = PaginationTableConfig.Create<StudentAssignmentsModel>({
			tableId:
				PaginationTableId['lunchbox.pages.overview.assignment.detail.table'],
			getPage$: this.paginationHelper.buildGetPage(),
			pagination: {
				sizes: 'infinite',
				initialSort: { columnId: 'class_title', descending: false },
			},
			controls: this.isStudentView()
				? {
						columns: false,
						refresh: false,
						margins: false,
						sortMinimized: false,
						smallText: false,
						wrapText: false,
						sticky: false,
					}
				: { columns: false, refresh: false },
		});
		this.tableConfig.tableReady$
			.pipe(
				tap((x) => {
					this.tableApi = x;
				}),
				takeUntil(this._unsubscribe$),
			)
			.subscribe();
	};

	private refreshTableResults = () => {
		this.paginationHelper.setSourceStream(
			this.assignmentDetails.pipe(
				filter((x) => x != null),
				take(1),
			),
		);
		return this.tableApi?.refresh();
	};

	private rescaleChart = () => {
		setTimeout(() => {
			if (this.graphContainer != null) {
				const width = this.graphContainer.nativeElement.clientWidth;

				// We need to retry
				if (width < 5) {
					this.rescaleChart();
					return;
				}

				this.view = undefined;
				this.cdr.detectChanges();
				this.view = [width, this.graphContainer.nativeElement.clientHeight];
				this.cdr.detectChanges();
			}
		});
	};

	public filterSearchDates = (shiftForward: boolean) => {
		const direction = shiftForward ? 7 : -7;
		this.fromDate = this.fromDate.add(direction, 'date');
		this.toDate = this.toDate.add(direction, 'date');
		this.getAssignmentData();

		this.atCurrentDate = false;
		if (this.toDate.epoch >= spacetime.now().epoch) {
			this.atCurrentDate = true;
		}
	};

	public getTotalValueOfSeries = (name: string) => {
		const series: LunchboxPagesOverviewNxgChartMultiModel =
			this.studentAssignmentsData.find((data) => {
				return data.name === name;
			});
		let total = 0;
		if (series) {
			series.series.forEach((classGroup) => {
				total += classGroup.value;
			});
		}
		return total;
	};

	private getAssignmentData = () => {
		this.loadingAssignments = true;
		this.studentAssignmentsData = [];

		this.assignmentDetails.next(null);
		this.refreshTableResults();

		this.noStudentData = true;
		this.hasError = false;

		this.usersService
			.getTimezone(this.currentStudentId())
			.pipe(
				switchMap((tz) => {
					this.timezone = this.isStudentView()
						? tz.timezone
						: tz.institute_timezone;

					const tzFromDate = calendarDate(
						this.fromDate,
						this.timezone,
					).toNativeDate();
					const tzToDate = calendarDate(
						this.toDate,
						this.timezone,
					).toNativeDate();

					return this.studentService.getSubmittedAssignments(
						this.currentStudentId(),
						tzFromDate,
						tzToDate,
					);
				}),
				finalize(() => (this.loadingAssignments = false)),
			)
			.subscribe({
				next: (assignments) => {
					const newChartData: LunchboxPagesOverviewNxgChartMultiModel[] = [];
					this.noStudentData = assignments.length < 1;
					this.assignmentDetails.next(assignments.slice());

					const uniqueClasses = assignments.filter((value, index, self) => {
						return (
							self
								.map((item) => item.class_title)
								.indexOf(value.class_title) === index
						);
					});

					// Iterate over the dates
					const tzToDate = calendarDate(this.toDate, this.timezone)
						.startOf('date')
						.hour(12);
					let guard = 0;

					// Setting all dates to hour 12 to avoid the DST area since dates get really messed up at DST
					for (
						let tzCheckDate = calendarDate(this.fromDate, this.timezone)
							.startOf('date')
							.hour(12);
						tzCheckDate.isBefore(tzToDate) || tzCheckDate.isEqual(tzToDate);
						tzCheckDate = tzCheckDate.add(1, 'date').hour(12)
					) {
						guard++;
						if (guard > 30) break;

						if (
							newChartData.find(
								(d) =>
									d.name ===
									tzCheckDate.format(`{day-short} {date-pad}`).toString(),
							)
						) {
							continue;
						}

						// Find assignments with the same time
						const assignmentsByDate = assignments.filter((a) => {
							return spacetime(a.submission_date, this.timezone)
								.startOf('date')
								.hour(12)
								.isEqual(tzCheckDate);
						});

						const multiModel: LunchboxPagesOverviewNxgChartMultiModel = {
							name: tzCheckDate.format(`{day-short} {date-pad}`).toString(),
							series: [],
						};

						uniqueClasses.forEach((uniqClass) => {
							multiModel.series.push({
								name: uniqClass.class_title,
								value: 0,
							});
						});

						// Group each model in the right assignment "bucket" for the day
						assignmentsByDate.forEach((assignment) => {
							multiModel.series.find((ser) => {
								return ser.name === assignment.class_title;
							}).value += 1;
						});

						newChartData.push(multiModel);
					}

					this.studentAssignmentsData = newChartData;
					this.formatColorOrder(uniqueClasses);

					this.rescaleChart();
				},
				error: (errors) => {
					console.error(mergeStrings(errors));
					this.hasError = true;
					this.rescaleChart();
				},
			});
	};

	private formatColorOrder = (assignments: StudentAssignmentsModel[]) => {
		this.colorOverride = [];
		assignments.forEach((uniqueClass) => {
			if (!this.colorMap.has(uniqueClass.class_title)) {
				this.colorMap.set(
					uniqueClass.class_title,
					this.colors[this.colorTicker],
				);
				this.colorTicker += 1;
				if (this.colors.length === this.colorTicker) {
					this.colorTicker = 0;
				}
			}

			this.colorOverride.push({
				name: uniqueClass.class_title,
				value: this.colorMap.get(uniqueClass.class_title),
			});
		});
	};

	ngOnDestroy() {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
